import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useDataQuery } from '@dhis2/app-runtime'
import { Card, Button, colors, IconChevronUp16,
         IconChevronDown16, IconWarning16, IconDownload16 } from '@dhis2/ui'
import StatusBadge from './StatusBadge'

// ─────────────────────────────────────────────────────────────
// ProgressBar (unchanged)
const ProgressBar = ({ value, height = '6px', borderRadius = '3px' }) => (
  <div style={{ width: '100%', height, backgroundColor: '#e5e7eb', borderRadius }}>
    <div style={{
      width: `${Math.min(Math.max(value || 0, 0), 100)}%`,
      height: '100%',
      backgroundColor: getProgressColor(value || 0),
      borderRadius,
      transition: 'width 0.3s ease'
    }} />
  </div>
)

const getProgressColor = (value) => {
  if (value >= 100) return '#28a745';
  if (value >= 80) return '#17a7a5';
  if (value >= 40) return '#ffc107';
  return '#dc3545';
};

// ─────────────────────────────────────────────────────────────
// Data Element IDs (unchanged)
const dataElementIds = {
  name: 'I0QtZkMbzxN', progress: 'AElpNxzTdRh', status: 'LuTwfuN34pc',
  dueDate: 'IY0xyOmlEr7', description: 'LR70eqTjB8m', objectiveId: 'plJovUlxfQ0',
  weekEnding: 'EiL3lPaL908', kpiId: 'uXeH0X0aaCl', achievements: 'j9FdNkmPpXM',
  challenges: 'WSQ9nuXPnNL', nextSteps: 'M7PG2p2XsDA',
  positionTei: 'SlgO60cMK3F', incumbentStaffId: 'OHKFI8GuxPs'
}

// ─────────────────────────────────────────────────────────────
// 🎯 PERFORMANCE SCORING (same logic, extracted for clarity)

const getDaysBetween = (date1, date2) => {
  if (!date1 || !date2) return null;
  return Math.ceil((new Date(date2) - new Date(date1)) / (1000 * 60 * 60 * 24));
};

const getTaskCompletionDays = (task) => {
  if (!task || task.progress < 100) return null
  return getDaysBetween(task.eventDate, task.completedAt || task.lastUpdated)
}

const calculateSpeedScore = (completedTasks) => {
  if (completedTasks.length === 0) return 0;
  
  const scores = completedTasks.map(task => {
    const created = task.eventDate;
    const due = task.dueDate;
    const completed = task.lastUpdated ? new Date(task.lastUpdated) : new Date();
    
    if (!created || !due) return 50; // neutral if missing dates
    
    const scheduled = getDaysBetween(created, due);
    const actual = getDaysBetween(created, completed);
    
    if (scheduled === null || actual === null || scheduled === 0) return 50;
    
    // 100 = on time, >100 = early, <50 = late
    const efficiency = ((scheduled - actual) / scheduled) * 50;
    return Math.max(0, Math.min(100, 50 + efficiency));
  });
  
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
};
   const calculatePerformanceScore = ({ completionRate, speedScore, completedCount }) => {
      const volumeBonus = Math.min(100, Math.log10(completedCount + 1) * 25);
      return Math.round(
        (completionRate * 0.50) + 
        (speedScore * 0.30) + 
        (volumeBonus * 0.20)
      );
  };


// ✅ SIMPLE VERSION - Groups by Position TEI directly
const aggregateStaffPerformance = (tasks, staffMapping) => {
  const stats = {}
  
  tasks.forEach(task => {
    // ✅ Use Position TEI as the canonical grouping key
    const positionTei = task.positionTei || task.tei
    
    // Get position info from mapping with safe fallbacks
    const positionInfo = staffMapping[positionTei] || {}
    
    // ✅ Use position TEI as staffId for grouping
    const staffId = positionTei
    
    if (!stats[staffId]) {
      stats[staffId] = {
        staffId,
        // ✅ Show position title if available, fallback to TEI snippet
        staffName: positionInfo.positionTitle || `Position ${positionTei?.slice(0, 8)}`,
        positionTitle: positionInfo.positionTitle || `Position ${positionTei?.slice(0, 8)}`,
        positionTei: positionTei,
        tasks: [],
        completedTasks: []
      }
    }
    
    stats[staffId].tasks.push(task)
    if (task.progress >= 100) stats[staffId].completedTasks.push(task)
  })
  
  // Calculate metrics (same as before)
  Object.values(stats).forEach(staff => {
    const total = staff.tasks.length
    const completed = staff.completedTasks.length
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0
    const speed = calculateSpeedScore(staff.completedTasks)
    
    staff.metrics = {
      totalTasks: total, 
      completedTasks: completed,
      completionRate: rate, 
      speedScore: speed,
      performanceScore: calculatePerformanceScore({
        completionRate: rate, 
        speedScore: speed, 
        completedCount: completed
      }),
      avgProgress: total > 0 
        ? Math.round(staff.tasks.reduce((s, t) => s + (t.progress || 0), 0) / total) 
        : 0
    }
  })
  
  return stats
}

const rankStaff = (staffStats) => 
  Object.values(staffStats)
    .sort((a, b) => {
      if (b.metrics.performanceScore !== a.metrics.performanceScore) 
        return b.metrics.performanceScore - a.metrics.performanceScore;
      if (b.metrics.completionRate !== a.metrics.completionRate) 
        return b.metrics.completionRate - a.metrics.completionRate;
      const aDays = Number.isFinite(a.metrics.avgCompletionDays) ? a.metrics.avgCompletionDays : Number.MAX_SAFE_INTEGER;
      const bDays = Number.isFinite(b.metrics.avgCompletionDays) ? b.metrics.avgCompletionDays : Number.MAX_SAFE_INTEGER;
      if (aDays !== bDays) return aDays - bDays;
      return b.metrics.completedTasks - a.metrics.completedTasks;
    })
    .map((s, i) => ({
      ...s, rank: i + 1,
      rankIcon: i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`
    }));

// ─────────────────────────────────────────────────────────────
// 🎨 MOBILE-FRIENDLY UI COMPONENTS

const RankBadge = ({ rank, score }) => {
  const getStyle = () => {
    if (score >= 80) return { bg: '#d4edda', text: '#155724', border: '#28a745' };
    if (score >= 60) return { bg: '#cce5ff', text: '#004085', border: '#007bff' };
    if (score >= 40) return { bg: '#fff3cd', text: '#856404', border: '#ffc107' };
    return { bg: '#f8d7da', text: '#721c24', border: '#dc3545' };
  };
  const style = getStyle();
  
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '8px 12px', borderRadius: 12,
      backgroundColor: style.bg, color: style.text,
      border: `2px solid ${style.border}`,
      fontWeight: '600', fontSize: '13px',
      minWidth: 'fit-content'
    }}>
      <span style={{ fontSize: '16px' }}>#{rank}</span>
      <span>{score} pts</span>
    </div>
  );
};

const MetricPill = ({ label, value, color = '#6c757d' }) => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    padding: '8px 12px', backgroundColor: '#f8f9fa',
    borderRadius: 8, minWidth: 70
  }}>
    <span style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>{label}</span>
    <span style={{ fontWeight: '700', fontSize: '16px', color }}>{value}</span>
  </div>
);

const TaskMiniCard = ({ task }) => (
  <div style={{
    padding: '10px 12px', marginBottom: 8,
    backgroundColor: '#fff', border: '1px solid #e9ecef',
    borderRadius: 8, borderLeft: `3px solid ${getProgressColor(task.progress)}`
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
      <div style={{ fontWeight: '500', fontSize: '13px', flex: 1 }}>{task.name}</div>
      <StatusBadge status={task.status} />
    </div>
    <div style={{ marginTop: 6 }}>
      <ProgressBar value={task.progress} height="4px" />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: '11px', color: '#6c757d' }}>
        <span>{task.progress}%</span>
        {task.progress >= 100 && Number.isFinite(task.completionDays) && (
          <span>Done in {task.completionDays}d</span>
        )}
        {task.progress < 100 && task.dueDate && <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>}
      </div>
    </div>
  </div>
);


const StaffPerformanceCard = ({ staff, expanded, onToggle }) => {
  const { staffName, positionTitle, metrics, tasks, rank, rankIcon } = staff;
  
  return (
    <Card style={{ 
      marginBottom: 12, 
      borderLeft: `4px solid ${getProgressColor(metrics.performanceScore)}`,
      transition: 'box-shadow 0.2s',
      ':hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }
    }}>
      {/* Header - always visible */}
      <div 
        onClick={onToggle}
        style={{ 
          padding: '12px 16px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 12,
          borderBottom: expanded ? '1px solid #e9ecef' : 'none'
        }}
      >
        {/* Rank */}
        <div style={{ 
          width: 36, height: 36, borderRadius: '50%',
          backgroundColor: '#007bff', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: '700', fontSize: '14px', flexShrink: 0
        }}>
          {rankIcon}
        </div>
        
        {/* Staff Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: 2 }}>
            {staffName}
          </div>
          <div style={{ fontSize: '12px', color: '#6c757d' }}>{positionTitle}</div>
        </div>
        
        {/* Score Badge */}
        <RankBadge rank={rank} score={metrics.performanceScore} />
        
        {/* Expand IconChevronUp16 */}
        <div style={{ flexShrink: 0, cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); onToggle(); }}>
          {expanded ? (
            <IconChevronUp16 color="#6c757d" />
          ) : (
            <IconChevronDown16 color="#6c757d" />
          )}
        </div>
      </div>
      {/* Expanded Details */}
      {expanded && (
        <div style={{ padding: '12px 16px' }}>
          {/* Metrics Row - wraps on mobile */}
          <div style={{ 
            display: 'flex', flexWrap: 'wrap', gap: 8, 
            justifyContent: 'space-around', marginBottom: 16 
          }}>
            <MetricPill label="Total" value={metrics.totalTasks} />
            <MetricPill label="Done" value={metrics.completedTasks} color="#28a745" />
            <MetricPill label="Rate" value={`${metrics.completionRate}%`} color="#007bff" />
            <MetricPill
              label="Avg Days"
              value={Number.isFinite(metrics.avgCompletionDays) ? metrics.avgCompletionDays : '-'}
              color={Number.isFinite(metrics.avgCompletionDays) && metrics.avgCompletionDays <= 7 ? '#28a745' : '#6c757d'}
            />
            <MetricPill 
              label="Speed" 
              value={metrics.speedScore >= 60 ? '⚡' : metrics.speedScore >= 40 ? '👍' : '🐌'} 
              color={metrics.speedScore >= 60 ? '#28a745' : metrics.speedScore >= 40 ? '#ffc107' : '#dc3545'}
            />
          </div>
          
          {/* Overall Progress */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: 4 }}>
              <span style={{ color: '#6c757d' }}>Avg. Progress</span>
              <span style={{ fontWeight: '600' }}>{metrics.avgProgress}%</span>
            </div>
            <ProgressBar value={metrics.avgProgress} />
          </div>
          
          {/* Task List */}
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', marginBottom: 8, color: '#495057' }}>
              Tasks ({tasks.length})
            </div>
            {tasks.slice(0, expanded === 'all' ? tasks.length : 3).map(task => (
              <TaskMiniCard key={task.id} task={task} />
            ))}
            {tasks.length > 3 && expanded !== 'all' && (
              <Button 
                small secondary 
                onClick={(e) => { e.stopPropagation(); onToggle('all'); }}
                style={{ width: '100%', marginTop: 8 }}
              >
                Show all {tasks.length} tasks
              </Button>
            )}
            {expanded === 'all' && tasks.length > 3 && (
              <Button 
                small secondary 
                onClick={(e) => { e.stopPropagation(); onToggle(true); }}
                style={{ width: '100%', marginTop: 8 }}
              >
                Show less
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────
// 🧩 MAIN COMPONENT

const RealTasks = ({ orgUnitId, performanceData }) => {
  const [staffReport, setStaffReport] = useState([])
  const [expandedStaff, setExpandedStaff] = useState({})
  const [positionMapping, setPositionMapping] = useState({})

  const taskQuery = useMemo(() => ({
    allTasks: {
      resource: 'events',
      params: {
        program: 'yiT00XKrrHP',
        programStage: 'Nv9nYNcBTdM',
        pageSize: 1000,
        fields: 'event,orgUnit,orgUnitName,eventDate,dueDate,trackedEntityInstance,dataValues[dataElement,value],lastUpdated',
        order: 'eventDate:desc', skipPaging: false
      }
    }
  }), [orgUnitId])

  const { loading, error, data, refetch } = useDataQuery(taskQuery)


const buildPositionMapping = useCallback((perfData) => {

  const safeData = JSON.parse(JSON.stringify(perfData));
  const mapping = {}
  
  console.log('🔄 Building simple staff/position mapping...')
  
  // Step 1: Map Staff TEIs by their username attribute
  if (safeData?.staff?.trackedEntityInstances) {
    safeData.staff.trackedEntityInstances.forEach(staff => {
      const tei = staff.trackedEntityInstance
      const username = staff.attributes?.find(a => a.attribute === 'g3RRIfDiQra')?.value
      const staffName = username || `Staff-${tei.slice(0,8)}`
      
      // Map by TEI and by username for flexible lookup
      mapping[tei] = { staffName, username, userId: null, positionTitle: 'Unassigned' }
      if (username) {
        mapping[username] = { staffName, username, userId: null, positionTitle: 'Unassigned' }
      }
      console.log(`✅ Staff: ${tei} -> username:"${username}"`)
    })
  }
  
  // Step 2: Map Position TEIs and link to staff by User UID
  if (safeData?.positions?.trackedEntityInstances) {
    safeData.positions.trackedEntityInstances.forEach(position => {
      const positionTei = position.trackedEntityInstance
      const positionTitle = position.attributes?.find(a => a.attribute === 'NWe61Sa41CS')?.value || `Position-${positionTei.slice(0,8)}`
      const incumbentUserUid = position.attributes?.find(a => a.attribute === 'V66IFsw5kq6')?.value
      
      // Find staff by matching username to position's incumbent UID
      // (assuming username in staff attribute matches the DHIS2 user's username)
      let matchedStaff = null
      if (incumbentUserUid) {
        // Try direct lookup by UID first
        matchedStaff = mapping[incumbentUserUid]
        // If not found, search for staff with matching username
        if (!matchedStaff) {
          const staffEntry = Object.values(mapping).find(s => s.username === incumbentUserUid)
          if (staffEntry) matchedStaff = staffEntry
        }
      }
      
      mapping[positionTei] = {
        staffName: matchedStaff?.staffName || (incumbentUserUid ? 'Position Holder' : 'Vacant'),
        username: matchedStaff?.username || incumbentUserUid,
        userId: incumbentUserUid,
        positionTitle,
        isPositionReference: true
      }
      
      // Update the matched staff entry with position info
      if (matchedStaff) {
        matchedStaff.positionTitle = positionTitle
      }
      
      console.log(`✅ Position: ${positionTitle} -> incumbent:"${incumbentUserUid}" -> staff:"${matchedStaff?.staffName || 'none'}"`)
    })
  }
  
  console.log(`📊 Final mapping: ${Object.keys(mapping).length} entries`)
  return mapping
}, [])


  useEffect(() => {
    if (performanceData) {
      // Use the simplified buildPositionMapping function
      const mapping = buildPositionMapping(performanceData)
      setPositionMapping(mapping)
    }
  }, [performanceData, buildPositionMapping])  // ← Note: dependency is now buildPositionMapping

   
   // ✅ ADD THIS INSIDE RealTasks component (before return)
const exportCSV = useCallback(() => {
  if (!staffReport || staffReport.length === 0) {
    alert('No performance data available to export')
    return
  }
  
  const headers = ['Rank', 'Staff Name', 'Position', 'Total Tasks', 'Completed', 'Completion Rate %', 'Performance Score']
  
  const rows = staffReport.map(s => [
    s.rank,
    `"${(s.staffName || '').replace(/"/g, '""')}"`,  // Escape quotes for CSV
    `"${(s.positionTitle || '').replace(/"/g, '""')}"`,
    s.metrics.totalTasks,
    s.metrics.completedTasks,
    s.metrics.completionRate,
    s.metrics.performanceScore
  ])
  
  const csvContent = [
    headers.join(','),
    ...rows.map(r => r.join(','))
  ].join('\n')
  
  // Create download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  
  link.href = url
  link.download = `staff-performance-${new Date().toISOString().slice(0, 10)}.csv`
  link.style.display = 'none'
  
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)  
}, [staffReport])  

 useEffect(() => {
  if (process.env.NODE_ENV === 'development') {
    const staffCounts = {}
    staffReport.forEach(s => {
      staffCounts[s.staffName] = (staffCounts[s.staffName] || 0) + 1
    })
    
    const duplicates = Object.entries(staffCounts).filter(([_, count]) => count > 1)
    if (duplicates.length > 0) {
      console.warn('⚠️ DUPLICATE STAFF DETECTED:', duplicates)
    } else {
      console.log('✅ No duplicate staff entries - consolidation successful!')
    }
  }
}, [staffReport])

  const extractTasksFromEvents = useCallback((eventsData) => {
    if (!eventsData?.events) return []
    return eventsData.events.map(event => {
      const dv = event.dataValues || []
      const getValue = (id) => dv.find(d => d.dataElement === id)?.value || ''
      const progress = parseInt(getValue(dataElementIds.progress) || 0)
      const dueDate = getValue(dataElementIds.dueDate)
      const incumbentStaffId = getValue(dataElementIds.incumbentStaffId)
      const positionTei = getValue(dataElementIds.positionTei)
      const objectiveId = getValue(dataElementIds.objectiveId)
      const completionDays = progress >= 100 ? getDaysBetween(event.eventDate, event.lastUpdated) : null
      
      return {
        id: event.event,
        name: getValue(dataElementIds.name) || 'Unnamed Task',
        progress, dueDate,
        description: getValue(dataElementIds.description),
        status: progress >= 100 ? 'Completed' : 
                dueDate && new Date(dueDate) < new Date() ? 'Behind Schedule' : 
                progress >= 80 ? 'On Track' : progress >= 40 ? 'At Risk' : 'Behind Schedule',
        eventDate: event.eventDate, lastUpdated: event.lastUpdated, completedAt: event.lastUpdated, completionDays,
        tei: event.trackedEntityInstance,
        incumbentStaffId,
        positionTei,
        objectiveId,
        staffIdentifier: incumbentStaffId || event.trackedEntityInstance,
        orgUnitName: event.orgUnitName
      }
    })
  }, [])

 
  useEffect(() => {
    if (data?.allTasks) {
      const tasks = extractTasksFromEvents(data.allTasks)
      // ✅ Pass tasks directly - mapping is optional fallback, not required
      const stats = aggregateStaffPerformance(tasks, positionMapping)
      setStaffReport(rankStaff(stats))
    }
  }, [data, positionMapping, extractTasksFromEvents])

  useEffect(() => {
    const interval = setInterval(refetch, 30000)
    return () => clearInterval(interval)
  }, [refetch])

  const toggleExpand = (staffId, mode) => {
    setExpandedStaff(prev => ({
      ...prev, 
      [staffId]: mode !== undefined ? mode : !prev[staffId]
    }))
  }

  // ─────────────────────────────────────────────────────────────
  // RENDER - MOBILE-FIRST CARD LAYOUT

  if (loading) return (
    <Card style={{ padding: 16 }}>
      <h3>📊 Loading Performance Report...</h3>
      <ProgressBar value={50} />
    </Card>
  )
  
  if (error) return (
    <Card style={{ padding: 16, backgroundColor: '#f8d7da' }}>
      <h3>⚠️ Error</h3>
      <div style={{ color: '#721c24' }}>{error.message}</div>
    </Card>
  )

  return (
    <div style={{ padding: '8px', maxWidth: '100%' }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 4px 16px', flexWrap: 'wrap', gap: 12 
      }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>🏆 Staff Performance</h3>
        {/* 
        <button
         style={{
                boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                color: 'white',
                backgroundColor: 'rgb(238, 181, 9)',
                borderRadius: '8px',
                border: 'none',
                width: '100px',
                height: '35px',
                cursor: 'pointer',
                margin: '10px'
            }}
           onClick={refetch}>
            ↻ Refresh
         </button>
         */}
      </div>
     
      {/* Summary Stats - horizontal scroll on mobile */}
      <div style={{ 
        display: 'flex', gap: 12, marginBottom: 16,
        overflowX: 'auto', paddingBottom: 4,
        scrollbarWidth: 'none', msOverflowStyle: 'none'
      }}>
        {[
          { label: 'Staff', value: staffReport.length },
          { label: 'Total Tasks', value: staffReport.reduce((s, x) => s + x.metrics.totalTasks, 0) },
          { label: 'Completed', value: staffReport.reduce((s, x) => s + x.metrics.completedTasks, 0), color: '#28a745' },
          { label: 'Avg Rate', value: staffReport.length > 0 
            ? Math.round(staffReport.reduce((s, x) => s + x.metrics.completionRate, 0) / staffReport.length) + '%' 
            : '0%' }
        ].map((stat, i) => (
          <div key={i} style={{
            flex: '0 0 auto', minWidth: 80, padding: '10px 14px',
            backgroundColor: '#fff', borderRadius: 10,
            border: '1px solid #e9ecef', textAlign: 'center'
          }}>
            <div style={{ fontSize: '11px', color: '#6c757d', textTransform: 'uppercase' }}>{stat.label}</div>
            <div style={{ fontWeight: '700', fontSize: '18px', color: stat.color || '#212529' }}>{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Ranked Staff Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {staffReport.map(staff => (
          <StaffPerformanceCard
            key={staff.staffId}
            staff={staff}
            expanded={expandedStaff[staff.staffId]}
            onToggle={(mode) => toggleExpand(staff.staffId, mode)}
          />
        ))}
      </div>

      <div style={{ 
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 4px 16px', flexWrap: 'wrap', gap: 12 
        }}>
                  
          <div style={{ display: 'flex', gap: 8 }}>
            {/* ✅ NEW: Export Button */}
            <button 
              style={{
                    boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                    color: 'white',
                    backgroundColor: 'rgb(16, 178, 38)',
                    borderRadius: '8px',
                    border: 'none',
                    width: '150px',
                    height: '35px',
                    cursor: 'pointer',
                    margin: '10px'
                }} 
              onClick={exportCSV}
              disabled={staffReport.length === 0}
              title="Export report to CSV"
            >
              <IconDownload16 /> Export CSV
            </button>
            
            {/* Existing Refresh Button 
            <button
             style={{
                    boxShadow: "0 2px 4px rgba(108, 105, 105, 0.5)",
                    color: 'white',
                    backgroundColor: 'rgb(238, 181, 9)',
                    borderRadius: '8px',
                    border: 'none',
                    width: '100px',
                    height: '35px',
                    cursor: 'pointer',
                    margin: '10px'
                }}
              onClick={refetch}>↻ Refresh
             </button>
             */}
             
          </div>
        </div>

     
        {staffReport.length === 0 && !loading && (
          <Card style={{ padding: 24, textAlign: 'center', backgroundColor: '#fff3cd' }}>
            {/* ✅ Correct warning icon */}
            <IconWarning16 color="#856404" style={{ marginBottom: 8, display: 'inline-block' }} />
            <div style={{ color: '#856404' }}>No task data found. Ensure tasks have Position TEI or Incumbent Staff ID assigned.</div>
          </Card>
        )}
            </div>
          )
    }

export default RealTasks
